/* eslint-disable eqeqeq */
import React, { useState, useEffect, useMemo } from "react";
import api from "../api"; 
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const CreateProjectPage = () => {
  const navigate = useNavigate();
  const selectedProjectId = localStorage.getItem("selectedProjectId");

  // State management
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]); // Contains EVERYONE (Unassigned + Assigned)
  
  // Roles
  const [maker, setMaker] = useState("");
  const [checker, setChecker] = useState("");
  const [approverPairs, setApproverPairs] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [assignedVendors, setAssignedVendors] = useState([]);
  const [selectedIssuerId, setSelectedIssuerId] = useState("");
  
  // Observer State
  const [selectedObserverId, setSelectedObserverId] = useState(""); 

  const [isUpdateMode, setIsUpdateMode] = useState(false);

  useEffect(() => {
    if (!selectedProjectId) {
      toast.error("No project selected. Please select a project first.");
      navigate("/dashboard");
      return;
    }

    const fetchProjectData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Unassigned Users
        const usersRes = await api.get(
          `/vres/projects/${selectedProjectId}/unassigned-users`
        );
        let fetchedUsers = (usersRes.data || []).map(u => ({
            ...u,
            userId: u.id || u.userId 
        }));

        // 2. Fetch Basic Project Info
        const projectRes = await api.get(`/vres/projects/${selectedProjectId}`);
        const projectData = projectRes.data;
        
        if (projectData) {
          setProjectName(projectData.title || "");
          setDescription(projectData.description || "");
          if (projectData.description) {
              setIsUpdateMode(true);
          }
        }

        // 3. Fetch Existing Team (The users who are ALREADY assigned)
        const teamRes = await api.get(`/vres/projects/${selectedProjectId}/team-details`);
        const teamData = teamRes.data || {};

        // --- MERGE LOGIC ---
        const assignedIssuers = (teamData.issuers || []).map(u => ({ 
            userId: u.id, name: u.name, email: u.email 
        }));
        const assignedObservers = (teamData.observers || []).map(u => ({ 
            userId: u.id, name: u.name, email: u.email 
        }));
        const assignedVendorsList = (teamData.vendors || []).map(u => ({
             userId: u.id, name: u.name, email: u.email
        }));

        // Combine into master list
        const usersToAdd = [...assignedIssuers, ...assignedObservers, ...assignedVendorsList];

        usersToAdd.forEach(assignedUser => {
            if (!fetchedUsers.find(u => u.userId == assignedUser.userId)) {
                fetchedUsers.push(assignedUser);
            }
        });

        setAllUsers(fetchedUsers); 

        // --- POPULATE STATE ---
        if (teamData.departments) {
            setApproverPairs(teamData.departments);
        }
        if (teamData.issuers && teamData.issuers.length > 0) {
            setSelectedIssuerId(teamData.issuers[0].id);
        }
        if (teamData.observers && teamData.observers.length > 0) {
            setSelectedObserverId(teamData.observers[0].id);
        }
        if (teamData.vendors) {
            setAssignedVendors(teamData.vendors.map(v => ({
                userId: v.id,
                name: v.name,
                gst: v.vendorGst || "", 
                address: v.vendorAddress || ""
            })));
        }

      } catch (err) {
        console.error("Error fetching project data:", err);
        if (err.response && err.response.status === 403) {
            toast.error("Access Denied: You do not have permission to view these users.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProjectData();
  }, [selectedProjectId, navigate]);

  // --- AVAILABLE USERS CALCULATION ---
  const availableUsers = useMemo(() => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const currentUserId = currentUser ? (currentUser.userId || currentUser.id) : null;

    const assignedUserIds = new Set([
      ...approverPairs.flatMap((p) => [Number(p.makerId), Number(p.checkerId)]),
      selectedIssuerId ? Number(selectedIssuerId) : null,
      ...assignedVendors.map((v) => Number(v.userId)),
      selectedObserverId ? Number(selectedObserverId) : null,
      currentUserId 
    ]);

    return allUsers.filter((user) => {
        if (!user) return false;
        // The dropdown filters out anyone assigned elsewhere.
        // NOTE: We handle the "Self-Select" (e.g. Issuer selecting themselves) in the JSX below.
        if (assignedUserIds.has(Number(user.userId))) return false;
        if (user.role && user.role.trim().toUpperCase() === "PROJECT COORDINATOR") return false;
        return true;
    });
  }, [allUsers, approverPairs, selectedIssuerId, assignedVendors, selectedObserverId]);

  // Find objects for display
  // We look in 'allUsers' (not availableUsers) to ensure we find them even if they are assigned
  const selectedIssuerObj = allUsers.find(u => u.userId == selectedIssuerId);
  const selectedObserverObj = allUsers.find(u => u.userId == selectedObserverId);

  const handleIssuerChange = (e) => setSelectedIssuerId(e.target.value);
  const handleObserverChange = (e) => setSelectedObserverId(e.target.value); 

  const handleAddApproverPair = () => {
    if (!maker || !checker)
      return toast.warn("Please select both a Maker and a Checker.");
    if (maker === checker)
      return toast.warn("Maker and Checker cannot be the same person.");
    
    const makerObj = allUsers.find((u) => u.userId == maker);
    const checkerObj = allUsers.find((u) => u.userId == checker);
    
    setApproverPairs((prev) => [
      ...prev,
      {
        makerId: makerObj.userId,
        makerName: makerObj.name,
        checkerId: checkerObj.userId,
        checkerName: checkerObj.name,
      },
    ]);
    setMaker("");
    setChecker("");
  };

  const handleRemoveApprover = (index) =>
    setApproverPairs((prev) => prev.filter((_, i) => i !== index));

  const handleAddVendor = () => {
    if (!selectedVendor) return toast.warn("Please select a vendor to add.");
    const vendorObj = allUsers.find((u) => u.userId == selectedVendor);
    setAssignedVendors((prev) => [
      ...prev,
      { userId: vendorObj.userId, name: vendorObj.name, gst: "", address: "" },
    ]);
    setSelectedVendor("");
  };

  const handleVendorDetailChange = (index, field, value) =>
    setAssignedVendors((prev) =>
      prev.map((vendor, i) =>
        i === index ? { ...vendor, [field]: value } : vendor
      )
    );

  const handleRemoveVendor = (index) =>
    setAssignedVendors((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (maker || checker) {
      toast.warn("You have selected a Maker/Checker but not combined them. Click 'Combine' or clear selection.");
      setLoading(false);
      return;
    }

    if (!projectName || !description) {
      toast.error("Please fill out all project detail fields.");
      setLoading(false);
      return;
    }

    const payload = {
      projectName,
      projectDescription: description,
      approvers: approverPairs.map((p) => ({
        makerId: p.makerId,
        checkerId: p.checkerId,
      })),
      issuerIds: selectedIssuerId ? [Number(selectedIssuerId)] : [],
      observerId: selectedObserverId ? Number(selectedObserverId) : null,
      vendors: assignedVendors.map((v) => ({
        userId: v.userId,
        gst: v.gst,
        address: v.address,
      })),
    };

    try {
      await api.put(`/vres/projects/${selectedProjectId}/details`, payload);
      
      if (isUpdateMode) {
          toast.success("Project updated successfully!");
      } else {
          toast.success("Project details saved and roles assigned successfully!");
          setIsUpdateMode(true);
      }
      window.scrollTo(0, 0);
      
    } catch (err) {
      console.error("Error submitting project details:", err);
      toast.error(err.response?.data?.message || "Failed to save project details.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !projectName) 
    return (
      <div style={{ textAlign: "center", padding: "50px", fontSize: "1.2em" }}>
        Loading project details...
      </div>
    );

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h2 style={styles.title}>
            {isUpdateMode ? "Update Project Details" : "Define Project Details & Assign Roles"}
        </h2>
        
        <form onSubmit={handleSubmit}>
          {/* 1. Project Information */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>1. Project Information</h3>
            <div style={styles.formGrid}>
              <div style={styles.fullWidth}>
                <label style={styles.label}>Project Name</label>
                <input
                  style={styles.input}
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                />
              </div>
              <div style={styles.fullWidth}>
                <label style={styles.label}>Project Description</label>
                <textarea
                  style={styles.textarea}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* 2. Departments (Maker & Checker) */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              2. Departments (Maker & Checker)
            </h3>
            <div style={styles.dropdownGroup}>
              <select
                style={styles.select}
                value={maker}
                onChange={(e) => setMaker(e.target.value)}
              >
                <option value="">-- Select Maker --</option>
                {availableUsers
                  .filter((u) => u.userId != checker)
                  .map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.name}
                    </option>
                  ))}
              </select>
              <select
                style={styles.select}
                value={checker}
                onChange={(e) => setChecker(e.target.value)}
              >
                <option value="">-- Select Checker --</option>
                {availableUsers
                  .filter((u) => u.userId != maker)
                  .map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                style={styles.btnPrimary}
                onClick={handleAddApproverPair}
              >
                Combine
              </button>
            </div>
            
            {/* Displaying the Chips */}
            <div style={styles.tagContainer}>
              {approverPairs.map((pair, index) => (
                <div style={styles.tag} key={index}>
                  <span>{pair.makerName} & {pair.checkerName}</span>
                  <span
                    style={styles.removeBtn}
                    onClick={() => handleRemoveApprover(index)}
                  >
                    ✕
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 3. Issuer */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>3. Issuer</h3>
            
            {/* 3A. The Dropdown (Always Visible) */}
            <select
              value={selectedIssuerId}
              onChange={handleIssuerChange}
              style={styles.input}
            >
              <option value="">-- Select an Issuer --</option>
              {availableUsers.map((user) => (
                <option key={user.userId} value={user.userId}>
                  {user.name} ({user.email})
                </option>
              ))}
              
              {/* IMPORTANT: If a user is selected, ensure they appear in the dropdown even if 'availableUsers' filtered them out */}
              {selectedIssuerId && !availableUsers.find(u => u.userId == selectedIssuerId) && selectedIssuerObj && (
                   <option value={selectedIssuerId}>
                       {selectedIssuerObj.name} ({selectedIssuerObj.email})
                   </option>
              )}
            </select>

            {/* 3B. The Chip (Visible only if selected) */}
            {selectedIssuerId && selectedIssuerObj && (
               <div style={styles.tagContainer}>
                   <div style={styles.tag}>
                       <span>{selectedIssuerObj.name} ({selectedIssuerObj.email})</span>
                       {/* Clicking X clears the state, which updates the dropdown to empty automatically */}
                       <span style={styles.removeBtn} onClick={() => setSelectedIssuerId("")}>✕</span>
                   </div>
               </div>
            )}
          </div>

          {/* 4. Observer */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>4. Observer</h3>
            
            {/* 4A. The Dropdown */}
            <select
              value={selectedObserverId}
              onChange={handleObserverChange}
              style={styles.input}
            >
              <option value="">-- Select an Observer (Optional) --</option>
              {availableUsers.map((user) => (
                <option key={user.userId} value={user.userId}>
                  {user.name} ({user.email})
                </option>
              ))}
               {selectedObserverId && !availableUsers.find(u => u.userId == selectedObserverId) && selectedObserverObj && (
                   <option value={selectedObserverId}>
                       {selectedObserverObj.name} ({selectedObserverObj.email})
                   </option>
              )}
            </select>

            {/* 4B. The Chip */}
            {selectedObserverId && selectedObserverObj && (
               <div style={styles.tagContainer}>
                   <div style={styles.tag}>
                       <span>{selectedObserverObj.name} ({selectedObserverObj.email})</span>
                       <span style={styles.removeBtn} onClick={() => setSelectedObserverId("")}>✕</span>
                   </div>
               </div>
            )}
          </div>

          {/* 5. Vendors */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>5. Vendors</h3>
            <div style={styles.dropdownGroup}>
              <select
                style={styles.select}
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
              >
                <option value="">-- Select a Vendor to Add --</option>
                {availableUsers.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                style={styles.btnPrimary}
                onClick={handleAddVendor}
              >
                Add Vendor
              </button>
            </div>
            <div>
              {assignedVendors.map((vendor, index) => (
                <div key={index} style={styles.vendorRow}>
                  <strong style={{ minWidth: "150px" }}>{vendor.name}</strong>
                  <input
                    style={styles.input}
                    placeholder="GST Number"
                    value={vendor.gst}
                    onChange={(e) =>
                      handleVendorDetailChange(index, "gst", e.target.value)
                    }
                    required
                  />
                  <input
                    style={styles.input}
                    placeholder="Address"
                    value={vendor.address}
                    onChange={(e) =>
                      handleVendorDetailChange(index, "address", e.target.value)
                    }
                    required
                  />
                  <span
                    style={{ ...styles.removeBtn, fontSize: "20px" }}
                    onClick={() => handleRemoveVendor(index)}
                  >
                    ✕
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div style={styles.buttonGroup}>
            <button
              type="button"
              style={styles.btnSecondary}
              onClick={() => navigate("/dashboard")}
            >
              Cancel
            </button>
            <button type="submit" style={styles.btnPrimary} disabled={loading}>
              {loading 
                ? "Processing..." 
                : (isUpdateMode ? "Update Project" : "Save Project & Assign Roles")
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  page: {
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
    padding: "20px",
  },
  container: {
    width: "90%",
    maxWidth: "800px",
    padding: "30px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    margin: "20px auto",
    fontFamily: "Arial, sans-serif",
  },
  title: { textAlign: "center", color: "#007bff", marginBottom: "24px" },
  section: {
    marginBottom: "25px",
    paddingBottom: "25px",
    borderBottom: "1px solid #e9ecef",
  },
  sectionTitle: { color: "#495057", marginBottom: "15px" },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
  },
  fullWidth: { gridColumn: "1 / -1" },
  label: { display: "block", marginBottom: "8px", fontWeight: "bold" },
  input: {
    width: "100%",
    padding: "10px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "10px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    minHeight: "80px",
    resize: "vertical",
    boxSizing: "border-box",
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "24px",
  },
  btnPrimary: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    backgroundColor: "#007bff",
    color: "#fff",
  },
  btnSecondary: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    backgroundColor: "#6c757d",
    color: "#fff",
  },
  tagContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "15px",
  },
  tag: {
    backgroundColor: "#e9ecef",
    borderRadius: "16px",
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    fontSize: "0.9em",
  },
  removeBtn: {
    marginLeft: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    color: "#dc3545",
  },
  dropdownGroup: { display: "flex", gap: "10px", alignItems: "center" },
  select: {
    flex: 1,
    padding: "10px",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    backgroundColor: "white",
  },
  vendorRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "10px",
  },
};

export default CreateProjectPage;